import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDownIcon, FacebookIcon, InstagramIcon, LinkIcon, MinusIcon, PlusIcon, ShieldCheckIcon, ShoppingBagIcon, StarIcon, TruckIcon, TwitterIcon } from 'lucide-react';
import { getProductBySlug, PRODUCTS } from '../data/products';
import { formatPrice, stockLabel } from '../utils/format';
import { useCart } from '../contexts/CartContext';
import { ProductCard } from '../components/ProductCard';
const SHARE = [{
  label: 'Compartir en Instagram',
  icon: InstagramIcon
}, {
  label: 'Compartir en Facebook',
  icon: FacebookIcon
}, {
  label: 'Compartir en X',
  icon: TwitterIcon
}, {
  label: 'Copiar enlace',
  icon: LinkIcon
}];
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const {
    addItem
  } = useCart();
  const [activeImage, setActiveImage] = useState(0);
  const [colorId, setColorId] = useState(product?.colors[0]?.id ?? 'black');
  const [quantity, setQuantity] = useState(1);
  const [openSection, setOpenSection] = useState<string | null>('desc');
  const [zoom, setZoom] = useState<{
    x: number;
    y: number;
  } | null>(null);
  React.useEffect(() => {
    setActiveImage(0);
    setQuantity(1);
    setColorId(product?.colors[0]?.id ?? 'black');
    setOpenSection('desc');
  }, [slug, product]);
  const related = useMemo(() => {
    const others = PRODUCTS.filter(p => p.id !== product?.id);
    const sameCategory = others.filter(p => p.category === product?.category);
    const rest = others.filter(p => p.category !== product?.category);
    return [...sameCategory, ...rest].slice(0, 4);
  }, [product]);
  if (!product) {
    return <div className="mx-auto max-w-3xl px-5 py-32 text-center" data-id="element-1151" data-mp="2e7u65n9tlsy">
        <h1 className="font-display text-5xl uppercase text-white" data-id="element-1152" data-mp="2i65edgvy37dr">
          Producto no encontrado
        </h1>
        <Link to="/productos" className="mt-8 inline-block bg-pink-500 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-1153" data-mp="2n72yhz0imbhw">
          Volver al catálogo
        </Link>
      </div>;
  }
  const stock = stockLabel(product.stock);
  const soldOut = product.stock === 0;
  const price = product.salePrice ?? product.price;
  const selectedColor = product.colors.find(c => c.id === colorId);
  const sections = [{
    id: 'desc',
    title: 'Descripción completa',
    body: product.description
  }, {
    id: 'how',
    title: 'Cómo usar',
    body: product.howToUse
  }, {
    id: 'ship',
    title: 'Envío y devoluciones',
    body: 'Envío gratis en compras mayores a $ 3.500. Entregas en Montevideo en 24-48 h y al interior en 2-4 días hábiles vía DAC. Tenés 30 días para cambios o devoluciones sin uso, con el embalaje original.'
  }];
  return <div className="w-full bg-black" data-id="element-1154" data-mp="35ppefq0bur0n">
      {/* BREADCRUMB */}
      <nav aria-label="Ruta de navegación" className="mx-auto w-full max-w-7xl px-5 pt-8 text-[11px] uppercase tracking-[0.16em] text-white/35 lg:px-8" data-id="element-1155" data-mp="30re2n9qh26fs">
        <Link to="/" className="hover:text-white" data-id="element-1156" data-mp="389lxgoir70id">
          Inicio
        </Link>
        <span className="mx-2" data-id="element-1157" data-mp="3d8ghtuwdcx4l">/</span>
        <Link to="/productos" className="hover:text-white" data-id="element-1158" data-mp="3i0q3gyqgbb12">
          Productos
        </Link>
        <span className="mx-2" data-id="element-1159" data-mp="3n02xq4jnjkdo">/</span>
        <span className="text-white/70" data-id="element-1160" data-mp="3mz1fng47xxmg">{product.name}</span>
      </nav>

      <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-10 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-14" data-id="element-1161" data-mp="2pddslltnkrnl">
        {/* LEFT: GALLERY */}
        <div data-id="element-1162" data-mp="2ubxc7fkrxrcs">
          <div className="relative aspect-square w-full overflow-hidden border border-white/10 bg-ink-800" onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          setZoom({
            x: (e.clientX - rect.left) / rect.width * 100,
            y: (e.clientY - rect.top) / rect.height * 100
          });
        }} onMouseLeave={() => setZoom(null)} data-id="element-1163" data-mp="2udi8paqnfkda">
            <img src={product.images[activeImage]} alt={product.name} className="h-full w-full object-cover transition-transform duration-200" style={zoom ? {
            transform: 'scale(1.9)',
            transformOrigin: `${zoom.x}% ${zoom.y}%`
          } : undefined} data-id="element-1164" data-mp="h5hb17zm0qey" />
            {product.salePrice && <span className="absolute left-4 top-4 bg-pink-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white" data-id="element-1165" data-mp="v3p4h5xft9ld">
                -
                {Math.round((product.price - product.salePrice) / product.price * 100)}
                %
              </span>}
            <span className="pointer-events-none absolute bottom-4 right-4 bg-black/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-white/60" data-id="element-1166" data-mp="1is8b7h0u9ebc">
              Pasá el mouse para ampliar
            </span>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-3" data-id="element-1167" data-mp="22fuqxpwjibgl">
            {product.images.map((img, i) => <button key={img + i} type="button" onClick={() => setActiveImage(i)} aria-label={`Ver imagen ${i + 1}`} aria-pressed={activeImage === i} className={`aspect-square overflow-hidden border transition-colors ${activeImage === i ? 'border-pink-500' : 'border-white/10 hover:border-white/40'}`} data-id="element-1168" data-mp="1oglijceksln6" data-mpi={i}>
                <img src={img} alt="" className="h-full w-full object-cover opacity-80 transition-opacity hover:opacity-100" data-id="element-1169" data-mp="19r5x8bxtd8ev" />
              </button>)}
          </div>
        </div>

        {/* RIGHT: INFO */}
        <div data-id="element-1170" data-mp="32fwv14q054v9">
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-pink-500" data-id="element-1171" data-mp="32ggckkbqpr8r">
            {product.categoryLabel}
          </span>
          <h1 className="mt-3 font-display text-5xl uppercase leading-none text-white sm:text-6xl" data-id="element-1172" data-mp="2xnnak47nox0d">
            {product.name}
          </h1>

          <div className="mt-4 flex items-center gap-4" data-id="element-1173" data-mp="2oos18j9bf6su">
            <div className="flex items-center gap-1" aria-label={`${product.rating} de 5`} data-id="element-1174" data-mp="2oqcwkb3getrr">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? 'fill-pink-500 text-pink-500' : 'text-white/20'}`} data-id="element-1175" data-mp="3hdgtrtwb23sz" />)}
            </div>
            <span className="text-xs text-white/45" data-id="element-1176" data-mp="20zgxcswo4wah">
              {product.rating} · {product.reviews} reseñas
            </span>
            <span className="flex items-center gap-1.5 text-xs" data-id="element-1177" data-mp="1vz30m70ip8rr">
              <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-1178" data-mp="1lwftcyh7olyl" />
              <span className={stock.text} data-id="element-1179" data-mp="1qv0jg52r1p9b">{stock.label}</span>
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3" data-id="element-1180" data-mp="1vu3npnawnrg8">
            <span className={`font-display text-5xl leading-none ${product.salePrice ? 'text-pink-500' : 'text-white'}`} data-id="element-1181" data-mp="1vsiuq248ogkh">
              {formatPrice(price)}
            </span>
            {product.salePrice && <span className="text-lg text-white/35 line-through" data-id="element-1182" data-mp="29kp9bjrqufht">
                {formatPrice(product.price)}
              </span>}
          </div>
          <p className="mt-2 text-xs text-white/40" data-id="element-1183" data-mp="2jjo8mbqmcys5">
            3 pagos sin interés de {formatPrice(Math.round(price / 3))}
          </p>

          <p className="mt-6 text-base leading-relaxed text-white/55" data-id="element-1184" data-mp="k7lt1cl48ojr">
            {product.shortDescription}
          </p>

          {/* COLOR */}
          <div className="mt-9" data-id="element-1185" data-mp="p7hxte1n0k0d">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white" data-id="element-1186" data-mp="2r2noi7evg86r">
              Color:{' '}
              <span className="font-medium text-white/50" data-id="element-1187" data-mp="2m32so5v00fy9">
                {selectedColor?.name}
              </span>
            </h2>
            <div className="mt-4 flex gap-3" data-id="element-1188" data-mp="30s7dlut6gya2">
              {product.colors.map(color => <button key={color.id} type="button" onClick={() => setColorId(color.id)} aria-label={color.name} aria-pressed={colorId === color.id} className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all ${colorId === color.id ? 'border-white' : 'border-white/20 hover:border-white/50'}`} data-id="element-1189" data-mp="2vup9chq3v78e">
                  <span className="h-7 w-7 rounded-full" style={{
                backgroundColor: color.hex
              }} data-id="element-1190" data-mp="3oiuw1a0pz6av" />
                </button>)}
            </div>
          </div>

          {/* QUANTITY */}
          <div className="mt-8" data-id="element-1191" data-mp="4xdr4akagivw">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white" data-id="element-1192" data-mp="3m3xuxx9csqjg">
              Cantidad
            </h2>
            <div className="mt-4 inline-flex items-center border border-white/15 bg-ink-800" data-id="element-1193" data-mp="3r27qwa2wjv64">
              <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} aria-label="Quitar una unidad" className="p-3.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white" data-id="element-1194" data-mp="imxrj9qed421">
                <MinusIcon className="h-4 w-4" data-id="element-1195" data-mp="dw7jtpui0p46" />
              </button>
              <span className="w-14 text-center font-display text-xl text-white" data-id="element-1196" data-mp="du4msluxhvpy">
                {quantity}
              </span>
              <button type="button" onClick={() => setQuantity(q => q + 1)} aria-label="Agregar una unidad" className="p-3.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white" data-id="element-1197" data-mp="16dblx6wgfezs">
                <PlusIcon className="h-4 w-4" data-id="element-1198" data-mp="1rubfwrrmwpkx" />
              </button>
            </div>
          </div>

          {/* CTAS */}
          <div className="mt-8 space-y-3" data-id="element-1199" data-mp="1ivg8jccut9g1">
            <button type="button" disabled={soldOut} onClick={() => addItem(product.id, colorId, quantity)} className={`flex w-full items-center justify-center gap-2 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${soldOut ? 'cursor-not-allowed bg-ink-600 text-white/35' : 'bg-pink-500 text-white hover:bg-pink-600'}`} data-id="element-1200" data-mp="1iueoiidv1cm6">
              <ShoppingBagIcon className="h-4 w-4" data-id="element-1201" data-mp="l98xbjm1oiup" />
              {soldOut ? 'Sin stock' : 'Agregar al carrito'}
            </button>
            <Link to="/checkout" onClick={() => !soldOut && addItem(product.id, colorId, quantity)} className="block w-full border border-white py-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-1202" data-mp="q9eokyoek3c9">
              Comprar ahora
            </Link>
          </div>

          <ul className="mt-8 grid gap-3 border-y border-white/10 py-6 text-sm text-white/50 sm:grid-cols-2" data-id="element-1203" data-mp="bjaadv4e2j4z">
            <li className="flex items-center gap-3" data-id="element-1204" data-mp="pi9y90k2uu03">
              <TruckIcon className="h-4 w-4 text-teal-500" data-id="element-1205" data-mp="ljkmfceotkik" />
              Envío gratis desde $ 3.500
            </li>
            <li className="flex items-center gap-3" data-id="element-1206" data-mp="z8uyesfeg6l4">
              <ShieldCheckIcon className="h-4 w-4 text-teal-500" data-id="element-1207" data-mp="ubcwhlzsurmm" />
              Garantía de 12 meses
            </li>
          </ul>

          {/* ACCORDION */}
          <div className="mt-8 border-t border-white/10" data-id="element-1208" data-mp="198lpsl8ny5gz">
            {sections.map(section => {
            const open = openSection === section.id;
            return <div key={section.id} className="border-b border-white/10" data-id="element-1209" data-mp="1bds2mlv8dq8w">
                  <h2 data-id="element-1210" data-mp="1gasdrb6xtj6v">
                    <button type="button" onClick={() => setOpenSection(open ? null : section.id)} aria-expanded={open} className="flex w-full items-center justify-between gap-4 py-5 text-left font-display text-lg uppercase tracking-[0.14em] text-white" data-id="element-1211" data-mp="1gc3f74cwueut">
                      {section.title}
                      <ChevronDownIcon className={`h-4 w-4 shrink-0 text-pink-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} data-id="element-1212" data-mp="1z0gspuue8p0l" />
                    </button>
                  </h2>
                  {open && <p className="pb-6 text-sm leading-relaxed text-white/55" data-id="element-1213" data-mp="9pg9grnc5rgr">
                      {section.body}
                    </p>}
                </div>;
          })}
          </div>

          {/* SHARE */}
          <div className="mt-8 flex items-center gap-4" data-id="element-1214" data-mp="2axlo8tc2nnx">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40" data-id="element-1215" data-mp="3ti9rjrorneoj">
              Compartir
            </span>
            <div className="flex gap-2" data-id="element-1216" data-mp="3ojyktptrpkaj">
              {SHARE.map(({
              label,
              icon: Icon
            }) => <button key={label} type="button" aria-label={label} className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 transition-colors hover:border-pink-500 hover:bg-pink-500 hover:text-white" data-id="element-1217" data-mp="q1t9yvykz0qf">
                  <Icon className="h-4 w-4" data-id="element-1218" data-mp="b3r2wbscbx4f" />
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* RELATED */}
      <section className="border-t border-white/10 bg-ink-900" data-id="element-1219" data-mp="2vtzxbyv81v7z">
        <div className="mx-auto w-full max-w-7xl px-5 py-16 lg:px-8 lg:py-24" data-id="element-1220" data-mp="2vwuijsm27k2g">
          <h2 className="font-display text-4xl uppercase leading-none text-white sm:text-5xl" data-id="element-1221" data-mp="oi01rpc8gyd3">
            Productos relacionados
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1222" data-mp="eln82rgoukic">
            {related.map(item => <ProductCard key={item.id} product={item} data-id="element-1223" data-mp="jjewhbi4johs" />)}
          </div>
        </div>
      </section>
    </div>;
}